import React, { useState } from "react";
import { ExternalLink, Mail, Search, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { rememberMyAd } from "@/lib/myAds";

const STATUS_LABEL = {
  active: "Aktivní",
  sold: "Prodáno",
  paused: "Pozastaveno",
  expired: "Vypršel",
};

function statusLabel(status) {
  return STATUS_LABEL[status] || status || "Aktivní";
}

export default function LookupPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {sent, found, items, mock, message}

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/listings/lookup", { email });
      const items = Array.isArray(data.items) ? data.items : [];
      items.forEach((it) => {
        if (it?.id && it?.manage_token) {
          rememberMyAd(it.id, {
            manage_token: it.manage_token,
            contact_email: email.trim(),
            title: it.title,
          });
        }
      });
      setResult({ ...data, items });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const items = result?.items || [];
  const empty = result && items.length === 0;

  return (
    <Layout>
      <div className="max-w-xl mx-auto stb-card p-6" data-testid="lookup-page">
        <h1 className="stb-h text-3xl text-white inline-flex items-center gap-2">
          <Search size={22} className="text-[#FF6B00]" /> Moje inzeráty
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2">
          Zadej e-mail, který jsi použil/a při vkládání inzerátů. Hned uvidíš
          seznam svých inzerátů s odkazy na úpravu.
        </p>

        {result ? (
          <div className="mt-5 space-y-4" data-testid="lookup-result">
            {empty ? (
              <div className="stb-card border-[#FF6B00]/50 p-4">
                <p className="stb-h text-[#FF6B00]" data-testid="lookup-empty">
                  K tomuto e-mailu jsme nenašli inzeráty
                </p>
                <p className="text-sm text-[#A3A3A3] mt-2">
                  Zkontroluj překlep, nebo zkus e-mail, pod kterým jsi inzerát
                  vložil/a.
                </p>
              </div>
            ) : (
              <>
                <div className="stb-card border-[#22C55E]/50 p-4">
                  <p className="stb-h text-[#22C55E]" data-testid="lookup-found">
                    Našli jsme {items.length}{" "}
                    {items.length === 1 ? "inzerát" : items.length < 5 ? "inzeráty" : "inzerátů"}
                  </p>
                  {result.sent && (
                    <p className="text-sm text-[#A3A3A3] mt-2 inline-flex items-start gap-2">
                      <Mail size={14} className="mt-0.5 shrink-0" />
                      Odkazy jsme poslali i na {email}.
                    </p>
                  )}
                  {result.mock && (
                    <p className="text-xs text-[#FFB800] mt-2 stb-mono">
                      MOCK: e-mail se neodeslal (Resend / doména).
                    </p>
                  )}
                </div>

                <ul className="space-y-2" data-testid="lookup-list">
                  {items.map((it) => {
                    const manage = it.manage_token
                      ? `/sprava-inzeratu/${it.id}?token=${encodeURIComponent(it.manage_token)}`
                      : `/sprava-inzeratu/${it.id}`;
                    return (
                      <li
                        key={it.id}
                        className="stb-card p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                        data-testid={`lookup-item-${it.id}`}
                      >
                        <div className="min-w-0">
                          <p className="stb-h text-white truncate">{it.title || "Bez názvu"}</p>
                          <p className="text-xs text-[#A3A3A3] mt-1">
                            Stav: {statusLabel(it.status)}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <Link
                            to={manage}
                            className="stb-btn stb-btn-primary text-xs"
                            data-testid={`lookup-manage-${it.id}`}
                          >
                            <Settings size={12} /> Spravovat
                          </Link>
                          <Link
                            to={`/inzerat/${it.id}`}
                            className="stb-btn stb-btn-secondary text-xs"
                            data-testid={`lookup-public-${it.id}`}
                          >
                            <ExternalLink size={12} /> Detail
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            <button
              onClick={() => { setResult(null); setError(""); }}
              className="stb-btn stb-btn-secondary"
              data-testid="lookup-again"
            >
              Hledat jiný e-mail
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <input
              className="stb-input"
              type="email"
              placeholder="tvuj@email.cz"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="lookup-email"
            />
            {error && (
              <p className="text-[#FF6B00] text-sm" data-testid="lookup-error">{error}</p>
            )}
            <button
              className="stb-btn stb-btn-primary w-full"
              disabled={busy}
              data-testid="lookup-submit"
            >
              <Search size={14} /> {busy ? "Hledám…" : "Najít moje inzeráty"}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}
